import { useEffect, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { DefaultComponentProps, OverridableTypeMap } from '@mui/material/OverridableComponent'
import { StaticImageData } from 'next/image'
import Link from 'next/link'

import { runClientEff } from '@ecm/effect'
import HowItWorksGridBlock from '@ecm/ui/component/block/HowItWorksGridBlock'
import Anchor from '@ecm/ui/element/Anchor'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import Player from '@ecm/ui/element/Player'

import actionIcon from '/public/images/icon/action.svg'
import businessDarkIcon from '/public/images/icon/business-dark.svg'
import businessIcon from '/public/images/icon/business.svg'
import checkDarkIcon from '/public/images/icon/check-dark.svg'
import checkIcon from '/public/images/icon/check.svg'
import globeDarkIcon from '/public/images/icon/globe-dark.svg'

type HowItWorksSectionProps = {
  children: string
  header: string
  icon: string
  largeIcon?: boolean
  width?: string
}

const HowItWorksSection = ({
  children,
  header,
  icon,
  largeIcon,
  width,
}: HowItWorksSectionProps) => (
  <Grid
    container
    sx={{ flexDirection: 'column', flexGrow: 0, padding: '1em 0', width: width ?? '23em' }}
  >
    <Grid item>
      <CloudflareImage
        src={`/images/icon/${icon}.svg`}
        height={largeIcon ? 128 : 72}
        width={largeIcon ? 128 : 72}
      />
    </Grid>
    <Typography variant="h5" component="h3">
      {header}
    </Typography>
    <Typography variant="p2">{children}</Typography>
  </Grid>
)

type HowItWorksProps = {
  backgroundColor?: string
  centerHeader?: boolean
  children: React.ReactNode
  header: string
  subHeader?: string
}

const HowItWorks = ({
  backgroundColor,
  centerHeader,
  children,
  header,
  subHeader,
}: HowItWorksProps) => (
  <Grid
    sx={{
      alignItems: 'center',
      background: backgroundColor,
      paddingY: { sm: '4em', xs: '2em' },
    }}
  >
    <MainContainer
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginX: 'auto',
      }}
    >
      {subHeader && (
        <Typography variant="h6" component="h4" color={Color.saint.normal}>
          {subHeader}
        </Typography>
      )}
      <Typography
        variant="h3"
        component="h2"
        textAlign={centerHeader ? 'center' : 'left'}
        maxWidth={centerHeader ? 'unset' : '15em'}
        padding={centerHeader ? '0 0 1.5em 0' : '0'}
      >
        {header}
      </Typography>

      <Grid container alignItems="flex-start" flexWrap="wrap" justifyContent="space-between">
        {children}
      </Grid>

      <Link href="/how-virtual-mail-works">
        <Anchor href="/how-virtual-mail-works" caret alignSelf="flex-end">
          See our full process
        </Anchor>
      </Link>
    </MainContainer>
  </Grid>
)

type HowItWorksBlockProps = {
  header?: string
  subHeading?: string
}

const HowItWorksWithExplainerVideoBlock = () => {
  const [videoUrl, setVideoUrl] = useState('')

  useEffect(() => {
    runClientEff(eff => {
      setVideoUrl(eff.ask().explainerVideoFileUrl)
    })
  }, [])

  return (
    <Grid bgcolor={Color.smoke.normal}>
      <MainContainer sx={{ margin: 'auto' }}>
        <Grid
          container
          rowGap={4}
          sx={{
            flexDirection: 'column',
            justifyContent: 'center',
            marginX: 'auto',
            maxWidth: '100%',
            paddingY: { md: '4em', sm: '4em', xs: '3em' },
          }}
        >
          {videoUrl !== '' && (
            // hiding the whole section for the time being
            <Grid
              container
              sx={{
                display: videoUrl ? 'flex' : 'none',
                flexDirection: { sm: 'row', xs: 'column' },
                flexWrap: 'nowrap',
              }}
            >
              <Grid container flexDirection="column" sx={{ mb: { xs: '2em' }, mr: { sm: '2em' } }}>
                <Typography variant="p3" fontWeight={700} color={Color.saint.normal}>
                  How Earth Class Mail Works
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  margin="0 0 0.5em 0"
                  maxWidth={{ md: '15em', xs: 'unset' }}
                >
                  How we make postal mail easier
                </Typography>

                <Typography variant="p2" maxWidth={{ md: '18em', xs: 'unset' }}>
                  With a virtual mailbox, you aren't tied to a physical location. Your postal mail
                  becomes efficient, paperless, and accessible anywhere.
                </Typography>
              </Grid>
              <Player
                id="explainer-video-player"
                url={videoUrl}
                posterUrl="../images/explainer-video-poster.svg"
              />
            </Grid>
          )}
          <HowItWorksGridBlock />
          <Grid container justifyContent="flex-end">
            <Anchor href="/pricing" caret>
              See our plans
            </Anchor>
          </Grid>
        </Grid>
      </MainContainer>
    </Grid>
  )
}

type HowItWorksDynamicBlock = {
  header: string
  explanations: Explanation[]
}

export type Explanation = {
  description: string
  header: string
  icon: string
  position: number
}

export const HowItWorksDynamicBlock = ({ header, explanations }: HowItWorksDynamicBlock) => (
  <HowItWorks centerHeader header={header}>
    {explanations.map((explanation: Explanation) => (
      <HowItWorksSection
        header={explanation.header}
        icon={explanation.icon}
        key={explanation.position}
      >
        {explanation.description}
      </HowItWorksSection>
    ))}
  </HowItWorks>
)

const HowMailForwardingWorksBlock = () => (
  <HowItWorks backgroundColor={Color.smoke.normal} header="How Mail Forwarding Works">
    <HowItWorksSection header="Choose a plan and address" icon="logomark">
      Choose a plan and select from one of 80+ addresses from our national network.
    </HowItWorksSection>

    <HowItWorksSection header="We scan your postal mail" icon="scan">
      All plans offer unlimited digital copies, but if you need a physical copy, we can ship it to
      you.
    </HowItWorksSection>

    <HowItWorksSection header="We forward your postal mail" icon="mail-forwarding">
      You control your shipment: what it includes, how to package it, timelines and insurance. We'll
      even help with customs forms.
    </HowItWorksSection>
  </HowItWorks>
)

const HowPartnershipWorksBlock = ({ header, subHeading }: HowItWorksBlockProps) => (
  <HowItWorks header={header || ''} subHeader={subHeading}>
    <HowItWorksSection header="" icon="scan" width="16em">
      We scan your mail so you can access it anywhere, 24/7.
    </HowItWorksSection>

    <HowItWorksSection header="" icon="mailbox-left" width="16em">
      Receive mail and packages safely at one of our 80+ US addresses.
    </HowItWorksSection>

    <HowItWorksSection header="" icon="check" width="16em">
      Never miss a payday with automated check deposits.
    </HowItWorksSection>

    <HowItWorksSection header="" icon="integrations" width="16em">
      Share documents on Dropbox, Google Drive, and other platforms.
    </HowItWorksSection>
  </HowItWorks>
)

type CheckoutGridItemProps = {
  className?: string
  header: string
  headerColor: string
  icon: StaticImageData
  imageSize: number
  paragraph: string
  paragraphColor: string
}

const CheckoutGridItem = ({
  header,
  headerColor,
  icon,
  imageSize,
  paragraph,
  paragraphColor,
}: CheckoutGridItemProps) => {
  return (
    <Grid container alignItems="start" columnSpacing={2} wrap="nowrap">
      <Grid item flexShrink={0}>
        <CloudflareImage src={icon} height={imageSize} width={imageSize} />
      </Grid>
      <Grid item>
        <Typography
          variant="p2"
          component="h4"
          fontWeight={700}
          margin="0.5em 0 0.2em 0"
          color={headerColor}
        >
          {header}
        </Typography>
        <Typography variant="p3" color={paragraphColor}>
          {paragraph}
        </Typography>
      </Grid>
    </Grid>
  )
}

type HowItWorksCheckoutBlock = {
  showDarkStyle?: boolean
}

const HowItWorksCheckoutBlock = ({ showDarkStyle }: HowItWorksCheckoutBlock) => {
  const itemStyleProps = showDarkStyle
    ? {
        headerColor: Color.white.normal,
        imageSize: 41,
        paragraphColor: '#A9ACC3',
      }
    : {
        headerColor: Color.saint.normal,
        imageSize: 72,
        paragraphColor: Color.saint.normal,
      }

  const headerProps: DefaultComponentProps<OverridableTypeMap> = showDarkStyle
    ? {
        color: Color.white.normal,
        component: 'h3',
        sx: { span: { fontWeight: 'normal' } },
        variant: 'h4',
      }
    : { component: 'h2', variant: 'p1' }
  return (
    <Grid container flexDirection="column">
      <Typography padding="1em 0" {...headerProps}>
        Let’s simplify <span>your postal mail</span>
      </Typography>
      <Grid container rowGap={3} flexDirection="column">
        <CheckoutGridItem
          header="Access postal mail anywhere"
          icon={showDarkStyle ? globeDarkIcon : actionIcon}
          paragraph="We convert your postal mail into digital scans that you can access anywhere."
          {...itemStyleProps}
        />
        <CheckoutGridItem
          header="Get paid faster with automated check deposit"
          icon={showDarkStyle ? checkDarkIcon : checkIcon}
          paragraph="We electronically endorse your checks and finalize your deposit slip."
          {...itemStyleProps}
        />
        <CheckoutGridItem
          header="Join thousands of businesses"
          icon={showDarkStyle ? businessDarkIcon : businessIcon}
          paragraph="Earth Class Mail is trusted by individuals, ambitious startups and enterprises of every size."
          {...itemStyleProps}
        />
      </Grid>
    </Grid>
  )
}

export default {
  Checkout: HowItWorksCheckoutBlock,
  ExplainerVideo: HowItWorksWithExplainerVideoBlock,
  MailForwarding: HowMailForwardingWorksBlock,
  Partnership: HowPartnershipWorksBlock,
}
